import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import useDelay from 'libs/hook/useDelay';
import { NOW_DELAY_SECONDS } from 'libs/const';
import { ObsId } from 'libs/types';
import { checkImage } from 'libs/utils';

const CamHeader = styled.div`
  height: 20px;
  margin-bottom: 10px;
  padding: 0 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ObsName = styled.span`
  font-size: 17px;
  font-weight: 600;
  color: #bdcacd;
`;

const SkycamDateTime = styled.div<{ isDelay?: boolean }>`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({
    isDelay,
    theme: {
      now: { status },
    },
  }) => {
    return isDelay ? status.ok : '#bdcacd';
  }};
`;

const Date = styled.span`
  font-weight: 600;
`;

const SkycamOutline = styled.div<{ isDelay?: boolean }>`
  width: 100%;
  background-color: #000000;
  border-radius: 100%;
  box-shadow: 0 0 10px 0px #000000;
  overflow: hidden;
  border: ${({
    isDelay,
    theme: {
      now: { status },
    },
  }) => {
    if (isDelay) return `1px solid ${status.ok}`;
    return '';
  }};
`;

const Image = styled.img`
  position: relative;
  display: block;
  width: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.01);
`;

const IMAGE_POSITION = {
  '0': {
    top: '-4px',
  },
  '1': {
    top: '-10px',
  },
  '2': {
    top: '-4px',
  },
  '3': {
    top: '-12px',
    left: '-2px',
  },
  '4': {
    top: '-4px',
    left: '-6px',
  },
  '5': {
    top: '-6px',
  },
};

const ErrorImage = styled.div`
  color: #ffffff;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
`;

const SkycamImage = React.memo(({ obsId, skycamPath }: { obsId: ObsId; skycamPath?: string }) => {
  const [imageStatus, setImageStatus] = useState<boolean>();

  useEffect(() => {
    (async () => {
      if (skycamPath) {
        const ok = await checkImage(skycamPath);
        setImageStatus(ok);
      }
    })();
  }, [skycamPath]);

  if (imageStatus === undefined) {
    return <Image src="/assets/default_image.png" alt="skycam" />;
  }

  if (!imageStatus) {
    return <Image src="/assets/error_image.png" alt="skycam" />;
  }

  return <Image src={skycamPath} alt="skycam" style={{ ...IMAGE_POSITION[obsId] }} />;
});

interface SkycamProps {
  obsId: ObsId;
  obsName: string;
  latestDate: string;
  latestTime: string;
  skycamPath?: string;
  updatedAt?: string;
}

const Skycam = ({ obsId, obsName, latestDate, latestTime, skycamPath, updatedAt }: SkycamProps) => {
  const { isDelay } = useDelay({ target: updatedAt, delayTime: NOW_DELAY_SECONDS, obsId: undefined });
  return (
    <div>
      <CamHeader>
        <ObsName>{obsName}</ObsName>
        <SkycamDateTime isDelay={isDelay}>
          <Date>{latestDate}</Date>
          <span>{latestTime}</span>
        </SkycamDateTime>
      </CamHeader>
      <SkycamOutline isDelay={isDelay}>
        <SkycamImage obsId={obsId} skycamPath={skycamPath} />
      </SkycamOutline>
    </div>
  );
};

export default Skycam;
